import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [tokens, setTokens] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Extract the secure tokens from the URL when the page loads
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Remove the '#' and parse the parameters
      const hashParams = new URLSearchParams(hash.substring(1));
      const access_token = hashParams.get('access_token');
      const refresh_token = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (type === 'recovery' && access_token) {
        setTokens({ access_token, refresh_token });
      } else {
        setError('Invalid or expired recovery link. Please request a new one.');
      }
    } else {
      setError('No secure tokens found. Please use the link from your email.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Industry Standard Password Validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.');
      setLoading(false);
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Send the tokens AND the new password to the backend
      await axios.post(`${baseURL}/api/auth/reset-password`, {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        newPassword: password
      });

      setMessage('Password updated successfully! Redirecting to login...');
      
      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <span className="text-blue-600 text-2xl">🔑</span>
        </div>
        
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Create New Password</h1>

        {message ? (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mt-4">
            ✅ {message}
          </div>
        ) : (
          <>
            <p className="text-slate-500 text-sm mb-6">
              Please enter your new strong password below.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm text-left">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 text-left">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  New Password
                </label>
                <input 
                  type="password" 
                  required
                  disabled={!tokens} // Disable input if the link is invalid
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)} 
                />
                <p className="text-xs text-slate-500 mt-1.5">
                  Must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number, and a special character.
                </p>
              </div>

              <button 
                type="submit" 
                disabled={loading || !tokens}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50 mt-2">
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}